import type {
  PatientViewDocument,
  PatientViewDocumentFlow,
  PatientViewDocumentMessage,
  PatientViewPatientCommunicationMessage,
} from '../types/patient-documents.types'

export type DocumentReadContext = 'documents' | 'messages'

/**
 * Returns whether the document should be considered unread for UI (badge, sorting).
 * - documents context: unread in document list (DocumentFlow unread, Message open, PatientCommunication = attachment not opened).
 * - messages context: unread in message list (PatientCommunication = any message not read). Other types use same as documents.
 */
export function isDocumentUnread(document: PatientViewDocument, context: DocumentReadContext = 'documents'): boolean {
  if (document.type === 'DocumentFlow') {
    return document.documentFlowStatus === 'unread'
  }
  if (document.type === 'PatientCommunication') {
    const pc = document as PatientViewPatientCommunicationMessage
    if (context === 'messages') {
      return pc.messages?.some((m) => m.readStatus?.patient === null) ?? false
    }
    return pc.attachmentReadAt == null
  }
  if (document.type === 'Message') {
    return (document as PatientViewDocumentMessage).status === 'open'
  }
  return false
}

/**
 * Returns a copy of the document with read state applied for optimistic updates.
 * scope: for PatientCommunication only - 'message' = mark conversation read, 'attachment' = mark attachment read. Default = both (message for BC).
 */
export function patchDocumentAsRead(
  document: PatientViewDocument,
  scope?: 'message' | 'attachment',
): PatientViewDocument {
  if (document.type === 'DocumentFlow') {
    const flow = document as PatientViewDocumentFlow
    return {
      ...flow,
      status: flow.status === 'open' ? 'completed' : flow.status,
      documentFlowStatus: flow.documentFlowStatus === 'unread' ? 'read' : flow.documentFlowStatus,
    }
  }

  if (document.type === 'PatientCommunication') {
    const pc = document as PatientViewPatientCommunicationMessage
    const now = new Date().toISOString()
    if (scope === 'attachment') {
      return { ...pc, attachmentReadAt: now }
    }
    return {
      ...pc,
      messages: (pc.messages ?? []).map((m) => ({
        ...m,
        readStatus: {
          ...m.readStatus,
          patient: m.readStatus?.patient ?? now,
        },
      })),
    }
  }

  if (document.type === 'Message') {
    const message = document as PatientViewDocumentMessage
    return {
      ...message,
      status: message.status === 'open' ? 'completed' : message.status,
    }
  }

  return document
}
